import { useEffect, useState } from "react";

const TYPE_MS = 90;
const DELETE_MS = 45;
const HOLD_MS = 1800;
const RESTART_MS = 500;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Types the version string out one character at a time, holds, deletes it,
 * then retypes — a terminal-style loop instead of the shimmer/glow effects
 * tried before. Falls back to the plain static string when the user has
 * requested reduced motion.
 */
interface VersionTypewriterProps {
  text: string;
  colorClass: string;
}

const VersionTypewriter = ({ text, colorClass }: VersionTypewriterProps) => {
  const [display, setDisplay] = useState(prefersReducedMotion() ? text : "");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let timeout: ReturnType<typeof setTimeout>;
    if (!deleting) {
      if (display.length < text.length) {
        timeout = setTimeout(
          () => setDisplay(text.slice(0, display.length + 1)),
          TYPE_MS,
        );
      } else {
        timeout = setTimeout(() => setDeleting(true), HOLD_MS);
      }
    } else if (display.length > 0) {
      timeout = setTimeout(
        () => setDisplay(text.slice(0, display.length - 1)),
        DELETE_MS,
      );
    } else {
      timeout = setTimeout(() => setDeleting(false), RESTART_MS);
    }
    return () => clearTimeout(timeout);
  }, [display, deleting, text]);

  return (
    <span className={`font-mono text-[11px] tracking-wide tabular-nums ${colorClass}`}>
      {display}
      <span className="version-cursor">▌</span>
    </span>
  );
};

export default VersionTypewriter;
